/**
 * WebshopPage - Schoonhuisshop fulfillment queue
 *
 * Master-detail layout: order list left, detail panel right.
 * Flow: Klaarmaken verzending → Ingepakt → Verzonden (by MyParcel)
 */

import { useEffect, useState } from 'react';
import { Badge, Spinner } from '../components/ui';
import { ForwardOrderDialog } from '../components/webshop/ForwardOrderDialog';
import { EditAddressDialog } from '../components/webshop/EditAddressDialog';
import { CancelWebshopOrderDialog } from '../components/webshop/CancelWebshopOrderDialog';
import { useWebshopStore } from '../stores';
import type { WebshopOrder, WebshopOrderStatus } from '../lib/salesforce';
import { PhoneNumber } from '../components/phone/PhoneNumber';
import clsx from 'clsx';

// ============================================================================
// Icons
// ============================================================================

const PackageIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
);

const PhoneIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
  </svg>
);

const EnvelopeIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
  </svg>
);

const MapPinIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
  </svg>
);

const PrinterIcon = ({ className = 'w-4 h-4' }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18.25 7.034V3.375" />
  </svg>
);

const TruckIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25m-2.25 0h-2.25m0 0V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875v11.25" />
  </svg>
);

const ClockIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ClipboardIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
  </svg>
);

const PencilIcon = () => (
  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zM19.5 14.25v4.75A1.75 1.75 0 0117.75 20.75H5.25A1.75 1.75 0 013.5 19V6.75A1.75 1.75 0 015.25 5h4.75" />
  </svg>
);

// ============================================================================
// Status & tab config
// ============================================================================

const statusConfig: Record<WebshopOrderStatus, { variant: 'warning' | 'info' | 'primary' | 'success' | 'danger'; label: string }> = {
  'Klaarmaken verzending': { variant: 'warning', label: 'Klaarmaken' },
  'Ingepakt': { variant: 'info', label: 'Ingepakt' },
  'Bij leverancier': { variant: 'warning', label: 'Bij leverancier' },
  'Verzonden': { variant: 'primary', label: 'Verzonden' },
  'Terugbetaald': { variant: 'danger', label: 'Terugbetaald' },
  'Geannuleerd': { variant: 'danger', label: 'Geannuleerd' },
};

const tabs: WebshopOrderStatus[] = ['Klaarmaken verzending', 'Ingepakt', 'Bij leverancier', 'Verzonden', 'Geannuleerd'];

const AUTO_CLOSE_DAYS = 10;

// ============================================================================
// Format helpers
// ============================================================================

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount);
}

function formatDate(isoDate: string): string {
  const d = new Date(isoDate);
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

const LABEL_BASE_URL = 'https://www.schoonhuisshop.nl/api/myparcel/label';

function getLabelUrl(shippingParcelId: string, startPosition = 1): string {
  return `${LABEL_BASE_URL}?id=${shippingParcelId}&format=A4&position=${startPosition}`;
}

function getSelectedLabelUrl(orders: WebshopOrder[], selectedIds: Set<string>, startPosition = 1): string | null {
  const ids = orders
    .filter((o) => selectedIds.has(o.id) && o.shippingParcelId)
    .map((o) => o.shippingParcelId);
  if (ids.length === 0) return null;
  return `${LABEL_BASE_URL}?id=${ids.join(';')}&format=A4&position=${startPosition}`;
}

function formatDimensions(width?: number | null, length?: number | null): string | null {
  if (!width && !length) return null;
  if (width && length) return `${width} x ${length} cm`;
  if (width) return `${width} cm breed`;
  return `${length} cm lang`;
}

/** Returns urgency level based on order age */
function getUrgency(createdDate: string): 'normal' | 'warning' | 'urgent' {
  const ageMs = Date.now() - new Date(createdDate).getTime();
  const ageHours = ageMs / (1000 * 60 * 60);
  if (ageHours > 48) return 'urgent';
  if (ageHours > 24) return 'warning';
  return 'normal';
}

function formatAge(createdDate: string): string {
  const ageMs = Date.now() - new Date(createdDate).getTime();
  const ageHours = Math.floor(ageMs / (1000 * 60 * 60));
  if (ageHours < 1) return 'Zojuist';
  if (ageHours < 24) return `${ageHours}u geleden`;
  const ageDays = Math.floor(ageHours / 24);
  return `${ageDays}d geleden`;
}

/** Filter out shipping cost line items from picklist */
function getPickableItems(order: WebshopOrder) {
  return order.items.filter((item) =>
    !item.name.toLowerCase().includes('verzendkosten')
  );
}

function getShippingItem(order: WebshopOrder) {
  return order.items.find((item) =>
    item.name.toLowerCase().includes('verzendkosten')
  );
}

// ============================================================================
// Sub-components
// ============================================================================

function LabelPositionPicker() {
  const { labelStartPosition, setLabelStartPosition } = useWebshopStore();

  // Grid shows column-first: 1=TL, 2=BL, 3=TR, 4=BR
  // Render order [1,3,2,4] in 2-col grid gives visual layout:
  //   1  3
  //   2  4
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted whitespace-nowrap">Startpositie:</span>
      <div className="grid grid-cols-2 gap-0.5 p-0.5 bg-slate-100 rounded">
        {[1, 3, 2, 4].map((pos) => (
          <button
            key={pos}
            onClick={() => setLabelStartPosition(pos)}
            className={clsx(
              'w-6 h-5 rounded text-[10px] font-medium transition-colors',
              pos === labelStartPosition
                ? 'bg-indigo-600 text-white shadow-sm'
                : 'bg-white text-slate-400 hover:bg-slate-50 hover:text-slate-600'
            )}
          >
            {pos}
          </button>
        ))}
      </div>
    </div>
  );
}

function StatusTabs() {
  const { activeTab, setActiveTab, orderCounts } = useWebshopStore();

  const countForTab = (tab: WebshopOrderStatus): number => {
    switch (tab) {
      case 'Klaarmaken verzending': return orderCounts.klaarmakenVerzending;
      case 'Ingepakt': return orderCounts.ingepakt;
      case 'Bij leverancier': return orderCounts.bijLeverancier;
      case 'Verzonden': return orderCounts.verzonden;
      case 'Geannuleerd': return orderCounts.geannuleerd;
      default: return 0;
    }
  };

  const tabLabels: Record<string, string> = {
    'Klaarmaken verzending': 'Klaarmaken',
    'Ingepakt': 'Ingepakt',
    'Bij leverancier': 'Bij leverancier',
    'Verzonden': 'Verzonden',
    'Geannuleerd': 'Geannuleerd',
  };

  return (
    <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
      {tabs.map((tab) => {
        const count = countForTab(tab);
        const isActive = activeTab === tab;
        return (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all',
              isActive
                ? 'bg-white text-main shadow-sm'
                : 'text-muted hover:text-main'
            )}
          >
            {tabLabels[tab] || tab}
            {count > 0 && (
              <span
                className={clsx(
                  'inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-semibold',
                  isActive
                    ? tab === 'Klaarmaken verzending' ? 'bg-amber-100 text-amber-700'
                      : tab === 'Ingepakt' ? 'bg-blue-100 text-blue-700'
                      : tab === 'Bij leverancier' ? 'bg-orange-100 text-orange-700'
                      : 'bg-slate-100 text-slate-600'
                    : 'bg-slate-200 text-slate-500'
                )}
              >
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function OrderCard({ order, isSelected, onClick, showUrgency, showCheckbox, isChecked, onToggleCheck }: {
  order: WebshopOrder;
  isSelected: boolean;
  onClick: () => void;
  showUrgency?: boolean;
  showCheckbox?: boolean;
  isChecked?: boolean;
  onToggleCheck?: () => void;
}) {
  const urgency = showUrgency ? getUrgency(order.createdDate) : 'normal';

  return (
    <div
      className={clsx(
        'w-full text-left p-4 border-b border-gray-100 transition-colors flex gap-3',
        isSelected ? 'bg-indigo-50 border-l-2 border-l-indigo-500' : 'hover:bg-gray-50',
        urgency === 'urgent' && !isSelected && 'border-l-2 border-l-red-400 bg-red-50/30',
        urgency === 'warning' && !isSelected && 'border-l-2 border-l-amber-400 bg-amber-50/30',
      )}
    >
      {showCheckbox && order.shippingParcelId && (
        <div className="flex items-start pt-0.5">
          <input
            type="checkbox"
            checked={isChecked}
            onChange={(e) => { e.stopPropagation(); onToggleCheck?.(); }}
            className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
          />
        </div>
      )}
      {showCheckbox && !order.shippingParcelId && (
        <div className="w-4 flex-shrink-0" />
      )}
      <button onClick={onClick} className="flex-1 text-left min-w-0">
        <div className="flex items-start justify-between mb-1">
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-main text-sm">{order.name}</span>
            {order.shippingParcelId && <PrinterIcon className="w-3.5 h-3.5 text-slate-400" />}
          </div>
          <Badge variant={statusConfig[order.status]?.variant || 'default'} className="text-xs">
            {statusConfig[order.status]?.label || order.status}
          </Badge>
        </div>
        <div className="flex items-center gap-1.5">
          <p className="text-sm text-main truncate">{order.customerName}</p>
          {order.fulfillmentType === 'Dropship' && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-orange-100 text-orange-700 flex-shrink-0">Dropship</span>
          )}
          {order.fulfillmentType === 'Gemengd' && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-yellow-100 text-yellow-700 flex-shrink-0">Gemengd</span>
          )}
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-sm font-medium text-main">{formatCurrency(order.amount)}</span>
          <span className="text-xs text-muted">{getPickableItems(order).length} {getPickableItems(order).length === 1 ? 'item' : 'items'}</span>
        </div>
        <div className="flex items-center justify-between mt-1">
          <div className="flex items-center gap-1">
            {urgency !== 'normal' && (
              <ClockIcon />
            )}
            <span className={clsx(
              'text-xs',
              urgency === 'urgent' ? 'text-red-600 font-medium' : urgency === 'warning' ? 'text-amber-600 font-medium' : 'text-muted'
            )}>
              {formatAge(order.createdDate)}
            </span>
          </div>
        </div>
      </button>
    </div>
  );
}

function OrderDetail({ order, onForward, onEditAddress, onCancel }: { order: WebshopOrder; onForward?: (order: WebshopOrder) => void; onEditAddress?: (order: WebshopOrder) => void; onCancel?: (order: WebshopOrder) => void }) {
  const { markAsPacked, markVerzondenViaDropship, createShipment, isUpdating, activeTab, labelStartPosition, error } = useWebshopStore();

  const handleMarkVerzonden = async () => {
    if (!window.confirm(`Order ${order.name} markeren als verzonden door leverancier?`)) return;
    try {
      await markVerzondenViaDropship(order.id);
    } catch {
      // Error al in store, UI banner toont 'm
    }
  };

  // Dagen sinds doorsturen — voor info-banner in 'Bij leverancier' tab
  const dropshipDaysSince = order.dropshipDoorgestuurdDatum
    ? Math.floor((Date.now() - new Date(order.dropshipDoorgestuurdDatum).getTime()) / 86_400_000)
    : null;
  const dropshipDaysRemaining = dropshipDaysSince !== null
    ? Math.max(0, AUTO_CLOSE_DAYS - dropshipDaysSince)
    : null;

  const handleCreateShipment = async () => {
    try {
      await createShipment(order.id);
    } catch {
      // Error already set in store, UI picks it up
    }
  };

  const address = [order.street, [order.postalCode, order.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const pickableItems = getPickableItems(order);
  const shippingItem = getShippingItem(order);
  const subtotal = pickableItems.reduce((sum, item) => sum + item.price, 0);
  const urgency = getUrgency(order.createdDate);

  // Gemengde order: het dropship-deel staat los van de pak-lifecycle. Zolang het
  // nog niet is doorgestuurd (en de order niet geannuleerd is) moet de actie
  // beschikbaar blijven — ook op de Ingepakt-tab — zodat de SEBO-regel niet
  // blijft hangen als de wasserij het eigen-verzending-deel eerst inpakt.
  const dropshipItems = order.items.filter((i) => i.dropship);
  const dropshipPartnerName = dropshipItems.find((i) => i.dropshipPartnerName)?.dropshipPartnerName ?? 'leverancier';
  const dropshipForwarded = !!order.dropshipDoorgestuurdDatum;
  const isCancelledStatus = order.status === 'Terugbetaald' || order.status === 'Geannuleerd';

  return (
    <div className="p-6 overflow-y-auto h-full">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-main">{order.name}</h2>
            {activeTab === 'Klaarmaken verzending' && urgency !== 'normal' && (
              <span className={clsx(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                urgency === 'urgent' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700',
              )}>
                <ClockIcon />
                {urgency === 'urgent' ? 'Urgent' : 'Wacht >24u'}
              </span>
            )}
          </div>
          <p className="text-sm text-muted">{formatDate(order.createdDate)}</p>
        </div>
        <div className="flex items-center gap-2">
          {order.fulfillmentType === 'Dropship' && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">Dropship</span>
          )}
          {order.fulfillmentType === 'Gemengd' && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">Gemengd</span>
          )}
          <Badge variant={statusConfig[order.status]?.variant || 'default'}>
            {order.status}
          </Badge>
        </div>
      </div>

      {/* Customer info */}
      <div className="space-y-3 mb-6">
        <h3 className="text-sm font-semibold text-main uppercase tracking-wider">Klant</h3>
        <p className="font-medium text-main">{order.customerName}</p>
        {address && (
          <div className="flex items-start gap-2 text-sm text-muted">
            <MapPinIcon />
            <span>
              {address}
              {order.country && order.country !== 'NL' && (
                <span className="ml-1 text-xs font-medium text-indigo-600">({order.country})</span>
              )}
            </span>
            {activeTab === 'Klaarmaken verzending' && (
              !order.shippingParcelId ? (
                <button
                  onClick={() => onEditAddress?.(order)}
                  className="text-xs text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1 ml-2"
                  title="Adres wijzigen"
                >
                  <PencilIcon /> Wijzigen
                </button>
              ) : (
                <span
                  className="text-xs text-slate-400 ml-2"
                  title="Annuleer eerst het verzendlabel"
                >
                  Vergrendeld
                </span>
              )
            )}
          </div>
        )}
        {order.customerPhone && (
          <div className="flex items-center gap-2 text-sm text-muted">
            <PhoneIcon />
            <PhoneNumber number={order.customerPhone} customerName={order.customerName} sourceContext="webshop-order" className="hover:text-accent" />
          </div>
        )}
        {order.customerEmail && (
          <div className="flex items-center gap-2 text-sm text-muted">
            <EnvelopeIcon />
            <a href={`mailto:${order.customerEmail}`} className="hover:text-accent truncate">{order.customerEmail}</a>
          </div>
        )}
      </div>

      {/* Picklist */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-main uppercase tracking-wider mb-3">
          {activeTab === 'Klaarmaken verzending' ? 'Picklijst' : 'Bestelling'}
        </h3>
        <div className="bg-slate-50 rounded-lg divide-y divide-slate-200">
          {pickableItems.map((item) => (
            <div key={item.id} className="flex items-start justify-between p-3">
              <div className="flex items-start gap-3">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-200 text-xs font-semibold text-slate-600 mt-0.5">
                  {item.quantity}
                </span>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-main">{item.name}</p>
                    {item.dropship && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-orange-100 text-orange-700">
                        → {item.dropshipPartnerName || 'leverancier'}
                      </span>
                    )}
                  </div>
                  {formatDimensions(item.width, item.length) && (
                    <p className="text-xs text-muted">{formatDimensions(item.width, item.length)}</p>
                  )}
                </div>
              </div>
              <span className="text-sm font-medium text-main">{formatCurrency(item.price)}</span>
            </div>
          ))}
          {/* Subtotal + shipping breakdown */}
          {shippingItem ? (
            <>
              <div className="flex items-center justify-between p-3 text-sm text-muted">
                <span>Subtotaal</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex items-center justify-between p-3 text-sm text-muted">
                <span>Verzendkosten</span>
                <span>{formatCurrency(shippingItem.price)}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-100 rounded-b-lg">
                <span className="text-sm font-semibold text-main">Totaal</span>
                <span className="text-sm font-semibold text-main">{formatCurrency(order.amount)}</span>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-between p-3 bg-slate-100 rounded-b-lg">
              <span className="text-sm font-semibold text-main">Totaal</span>
              <span className="text-sm font-semibold text-main">{formatCurrency(order.amount)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Notes */}
      {order.notes && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-main uppercase tracking-wider mb-2">Opmerkingen</h3>
          <p className="text-sm text-muted bg-amber-50 border border-amber-200 rounded-lg p-3">{order.notes}</p>
        </div>
      )}

      {/* Tracking */}
      {(order.trackingNumber || order.shippingStatus) && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-main uppercase tracking-wider mb-2">Verzending</h3>
          <div className="space-y-1 text-sm">
            {order.trackingNumber && (
              <div className="flex items-center gap-2 text-muted">
                <span>Tracking:</span>
                <span className="font-mono text-main">{order.trackingNumber}</span>
                {order.trackingUrl && (
                  <a href={order.trackingUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700">
                    <ExternalLinkIcon />
                  </a>
                )}
              </div>
            )}
            {order.shippingStatus && (
              <p className="text-muted">Status: <span className="text-main">{order.shippingStatus}</span></p>
            )}
          </div>
        </div>
      )}

      {/* Info banner for Ingepakt tab */}
      {activeTab === 'Ingepakt' && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
          <TruckIcon />
          <p className="text-sm text-blue-700">
            Wacht op ophaling door PostNL. Status wordt automatisch bijgewerkt.
          </p>
        </div>
      )}

      {/* Info banner for Bij leverancier tab */}
      {activeTab === 'Bij leverancier' && (
        <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-start gap-2">
          <TruckIcon />
          <div className="text-sm text-orange-800">
            <p className="font-medium">Doorgestuurd naar leverancier</p>
            {order.dropshipDoorgestuurdDatum ? (
              <p className="mt-0.5">
                {formatDate(order.dropshipDoorgestuurdDatum)}
                {dropshipDaysRemaining !== null && (
                  <> · auto-close over {dropshipDaysRemaining} dag{dropshipDaysRemaining === 1 ? '' : 'en'}</>
                )}
              </p>
            ) : (
              <p className="mt-0.5">Wacht op leveranciersverzending.</p>
            )}
          </div>
        </div>
      )}

      {/* Dropship info banner — volledige Dropship */}
      {order.fulfillmentType === 'Dropship' && (
        <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-3">
          <p className="text-sm font-medium text-orange-800 mb-1">
            Volledige dropship bestelling
          </p>
          <p className="text-sm text-orange-700">
            Doorsturen naar leverancier voor rechtstreekse verzending aan klant.
          </p>
        </div>
      )}

      {/* Gemengd: dropship-deel staat los van het inpakken. Toont status +
          doorstuuractie, ongeacht de tab, zodat de leverancier-regel niet
          blijft hangen als het eigen-verzending-deel eerst wordt ingepakt. */}
      {order.fulfillmentType === 'Gemengd' && !isCancelledStatus && (
        <div className={clsx(
          'mb-6 rounded-lg p-3 border',
          dropshipForwarded ? 'bg-emerald-50 border-emerald-200' : 'bg-orange-50 border-orange-200',
        )}>
          {dropshipForwarded ? (
            <div className="flex items-start gap-2">
              <span className="text-emerald-600 mt-0.5"><CheckCircleIcon /></span>
              <div className="text-sm text-emerald-800">
                <p className="font-medium">Dropship-items doorgestuurd naar {dropshipPartnerName}</p>
                {order.dropshipDoorgestuurdDatum && (
                  <p className="mt-0.5">{formatDate(order.dropshipDoorgestuurdDatum)}</p>
                )}
              </div>
            </div>
          ) : (
            <>
              <p className="text-sm font-medium text-orange-800 mb-1">
                ⚠️ Bevat dropship-items — nog niet doorgestuurd
              </p>
              <p className="text-sm text-orange-700 mb-3">
                {dropshipItems.map((i) => `${i.quantity}× ${i.name}`).join(', ')} → {dropshipPartnerName} verstuurt rechtstreeks. De rest pak je zelf in.
              </p>
              <button
                onClick={() => onForward?.(order)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors"
              >
                <EnvelopeIcon />
                Dropship-items doorsturen
              </button>
            </>
          )}
        </div>
      )}

      {/* Geannuleerd-tab: read-only audit-info, geen acties */}
      {(activeTab === 'Geannuleerd') && (
        <div className="pt-4 border-t border-gray-100">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-600">
            <p className="font-medium text-slate-700 mb-1">
              {order.status === 'Terugbetaald' ? 'Terugbetaald via Mollie' : 'Geannuleerd zonder refund'}
            </p>
            {order.molliePaymentId && (
              <p className="text-xs text-slate-500">
                Mollie-payment: <span className="font-mono">{order.molliePaymentId}</span>
                {order.mollieStatus && <> · status <span className="font-medium">{order.mollieStatus}</span></>}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t border-gray-100">
        {/* Klaarmaken tab: Label printen + Ingepakt markeren (only for own-stock) */}
        {activeTab === 'Klaarmaken verzending' && order.fulfillmentType !== 'Dropship' && (
          <>
            {order.shippingParcelId ? (
              <button
                onClick={() => window.open(getLabelUrl(order.shippingParcelId!, labelStartPosition), '_blank')}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <PrinterIcon />
                Label printen
              </button>
            ) : (
              <div className="flex flex-col gap-2 max-w-md">
                <button
                  onClick={handleCreateShipment}
                  disabled={isUpdating}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors self-start"
                >
                  {isUpdating ? <Spinner className="w-4 h-4" /> : <PrinterIcon />}
                  Verzendlabel aanmaken
                </button>
                {error && !isUpdating && (
                  <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    {error}
                  </div>
                )}
              </div>
            )}
            <button
              onClick={() => markAsPacked(order.id)}
              disabled={isUpdating}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 disabled:opacity-50 transition-colors self-start"
            >
              {isUpdating ? <Spinner className="w-4 h-4" /> : <CheckCircleIcon />}
              Ingepakt
            </button>
          </>
        )}

        {/* Klaarmaken tab: Dropship — forward to supplier + copy backup */}
        {activeTab === 'Klaarmaken verzending' && order.fulfillmentType === 'Dropship' && (
          <>
            <button
              onClick={() => onForward?.(order)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors"
            >
              <EnvelopeIcon />
              Naar leverancier sturen
            </button>
            <button
              onClick={() => {
                const items = getPickableItems(order);
                const text = [
                  `Bestelling: ${order.name}`,
                  `Klant: ${order.customerName}`,
                  `Adres: ${address}`,
                  order.customerPhone ? `Tel: ${order.customerPhone}` : '',
                  order.customerEmail ? `Email: ${order.customerEmail}` : '',
                  '',
                  'Producten:',
                  ...items.map((i) => `  ${i.quantity}x ${i.name}`),
                  order.notes ? `\nOpmerking: ${order.notes}` : '',
                ].filter(Boolean).join('\n');
                navigator.clipboard.writeText(text);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
            >
              <ClipboardIcon />
              Kopieer bestelgegevens
            </button>
          </>
        )}

        {/* Bij leverancier tab: handmatige afsluiting */}
        {activeTab === 'Bij leverancier' && (
          <button
            onClick={handleMarkVerzonden}
            disabled={isUpdating}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {isUpdating ? <Spinner className="w-4 h-4" /> : <TruckIcon />}
            Markeer als verzonden
          </button>
        )}

        {/* Ingepakt tab: Label herprinten */}
        {activeTab === 'Ingepakt' && order.shippingParcelId && (
          <button
            onClick={() => window.open(getLabelUrl(order.shippingParcelId!, labelStartPosition), '_blank')}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 border border-slate-200 rounded-lg hover:bg-slate-200 transition-colors"
          >
            <PrinterIcon />
            Label herprinten
          </button>
        )}

        {/* Verzonden tab: Track link */}
        {activeTab === 'Verzonden' && order.trackingUrl && (
          <a
            href={order.trackingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
          >
            <ExternalLinkIcon />
            Track & Trace
          </a>
        )}

        {/* Cancel-knop: Klaarmaken + Ingepakt + Bij leverancier; rechts uitlijnen */}
        {(activeTab === 'Klaarmaken verzending' || activeTab === 'Ingepakt' || activeTab === 'Bij leverancier') && onCancel && (
          <button
            onClick={() => onCancel(order)}
            disabled={isUpdating}
            className="ml-auto inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"
          >
            Annuleren
          </button>
        )}
      </div>
    </div>
  );
}

function EmptyState({ tab }: { tab: WebshopOrderStatus }) {
  const messages: Record<WebshopOrderStatus, string> = {
    'Klaarmaken verzending': 'Geen bestellingen om in te pakken',
    'Ingepakt': 'Geen ingepakte bestellingen',
    'Bij leverancier': 'Geen openstaande dropship-bestellingen',
    'Verzonden': 'Geen verzonden bestellingen',
    'Terugbetaald': 'Geen terugbetaalde bestellingen',
    'Geannuleerd': 'Geen geannuleerde bestellingen',
  };

  const icons: Record<string, JSX.Element> = {
    'Klaarmaken verzending': <PackageIcon />,
    'Ingepakt': <CheckCircleIcon />,
    'Bij leverancier': <TruckIcon />,
    'Verzonden': <TruckIcon />,
    'Geannuleerd': <PackageIcon />,
  };

  return (
    <div className="flex flex-col items-center justify-center h-full text-center p-8">
      <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
        {icons[tab] || <PackageIcon />}
      </div>
      <p className="text-muted">{messages[tab]}</p>
    </div>
  );
}

// ============================================================================
// Main page
// ============================================================================

export function WebshopPage() {
  const {
    filteredOrders,
    selectedOrderId,
    selectedOrder,
    activeTab,
    isLoading,
    isUpdating,
    error,
    orderCounts,
    selectOrder,
    markAllAsPacked,
    selectedForPrintIds,
    toggleSelectForPrint,
    selectAllForPrint,
    clearPrintSelection,
    labelStartPosition,
    startPolling,
    stopPolling,
  } = useWebshopStore();

  const [forwardDialogOrder, setForwardDialogOrder] = useState<WebshopOrder | null>(null);
  const [editAddressOrder, setEditAddressOrder] = useState<WebshopOrder | null>(null);
  const [cancelDialogOrder, setCancelDialogOrder] = useState<WebshopOrder | null>(null);

  useEffect(() => {
    startPolling();
    return () => stopPolling();
  }, [startPolling, stopPolling]);

  const handleMarkAllAsPacked = () => {
    // Zelfde selectie als de store: pure dropship wordt doorgestuurd, niet ingepakt.
    const count = useWebshopStore.getState().orders.filter(
      (o) => o.status === 'Klaarmaken verzending' && o.fulfillmentType !== 'Dropship'
    ).length;
    if (count === 0) return;
    if (!window.confirm(`${count} bestelling${count > 1 ? 'en' : ''} markeren als ingepakt?`)) return;
    markAllAsPacked();
  };

  const printableCount = filteredOrders.filter((o) => o.shippingParcelId).length;
  const selectedPrintCount = selectedForPrintIds.size;
  const allPrintableSelected = printableCount > 0 && filteredOrders
    .filter((o) => o.shippingParcelId)
    .every((o) => selectedForPrintIds.has(o.id));

  const selectedLabelUrl = getSelectedLabelUrl(filteredOrders, selectedForPrintIds, labelStartPosition);

  // Bulk label printen kan in Klaarmaken (printen vóór inpakken) én Ingepakt
  // (label herprinten ná inpakken). Ingepakte orders hebben altijd een parcel/label.
  const showPrintSelection = activeTab === 'Klaarmaken verzending' || activeTab === 'Ingepakt';
  const showLabelControls =
    (activeTab === 'Klaarmaken verzending' && orderCounts.klaarmakenVerzending > 0) ||
    (activeTab === 'Ingepakt' && printableCount > 0);

  return (
    <>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-heading font-semibold text-main">Webshop Bestellingen</h1>
            {isLoading && <Spinner className="w-4 h-4" />}
          </div>
          <div className="flex items-center gap-3">
            <StatusTabs />
            {/* Label-print besturing: Klaarmaken (printen) + Ingepakt (herprinten) */}
            {showLabelControls && (
              <>
                <LabelPositionPicker />
                {/* Print selected labels (A4, 4 per page) */}
                {selectedPrintCount > 0 && selectedLabelUrl && (
                  <button
                    onClick={() => window.open(selectedLabelUrl, '_blank')}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    <PrinterIcon />
                    Print {selectedPrintCount} label{selectedPrintCount > 1 ? 's' : ''}
                  </button>
                )}
              </>
            )}
            {/* "Alles ingepakt" blijft Klaarmaken-only */}
            {activeTab === 'Klaarmaken verzending' && orderCounts.klaarmakenVerzending > 0 && (
              <button
                onClick={handleMarkAllAsPacked}
                disabled={isUpdating}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 disabled:opacity-50 transition-colors"
              >
                {isUpdating ? <Spinner className="w-4 h-4" /> : <CheckCircleIcon />}
                Alles ingepakt
              </button>
            )}
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="px-6 py-3 bg-red-50 border-b border-red-200 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Content: master-detail */}
        <div className="flex flex-1 overflow-hidden">
          {/* Order list */}
          <div className="w-80 border-r border-gray-200 overflow-y-auto bg-white flex-shrink-0">
            {/* Select all header (Klaarmaken + Ingepakt, met printbare orders) */}
            {showPrintSelection && printableCount > 0 && (
              <div className="flex items-center gap-3 px-4 py-2 border-b border-gray-100 bg-slate-50">
                <input
                  type="checkbox"
                  checked={allPrintableSelected}
                  onChange={selectAllForPrint}
                  className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                />
                <span className="text-xs text-muted">
                  {selectedPrintCount > 0
                    ? `${selectedPrintCount} geselecteerd`
                    : 'Selecteer voor printen'}
                </span>
                {selectedPrintCount > 0 && (
                  <button onClick={clearPrintSelection} className="text-xs text-indigo-600 hover:text-indigo-700 ml-auto">
                    Wissen
                  </button>
                )}
              </div>
            )}
            {isLoading && filteredOrders.length === 0 ? (
              <div className="flex justify-center py-12">
                <Spinner />
              </div>
            ) : filteredOrders.length === 0 ? (
              <EmptyState tab={activeTab} />
            ) : (
              filteredOrders.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  isSelected={order.id === selectedOrderId}
                  onClick={() => selectOrder(order.id)}
                  showUrgency={activeTab === 'Klaarmaken verzending'}
                  showCheckbox={showPrintSelection}
                  isChecked={selectedForPrintIds.has(order.id)}
                  onToggleCheck={() => toggleSelectForPrint(order.id)}
                />
              ))
            )}
          </div>

          {/* Detail panel */}
          <div className="flex-1 overflow-hidden bg-white">
            {selectedOrder ? (
              <OrderDetail
                order={selectedOrder}
                onForward={setForwardDialogOrder}
                onEditAddress={setEditAddressOrder}
                onCancel={setCancelDialogOrder}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-muted">
                <PackageIcon />
                <p className="mt-3 text-sm">Selecteer een bestelling</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <ForwardOrderDialog
        order={forwardDialogOrder}
        isOpen={!!forwardDialogOrder}
        onClose={() => setForwardDialogOrder(null)}
      />

      <EditAddressDialog
        order={editAddressOrder}
        isOpen={!!editAddressOrder}
        onClose={() => setEditAddressOrder(null)}
      />

      <CancelWebshopOrderDialog
        order={cancelDialogOrder}
        isOpen={!!cancelDialogOrder}
        onClose={() => setCancelDialogOrder(null)}
      />
    </>
  );
}
